import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useI18n } from '../../context/I18nContext';
import './CreditUnionBlueprint.css';
import {
  CREDIT_UNION_BLUEPRINT_FALLBACK,
  CREDIT_UNION_BLUEPRINT_SLUGS,
} from './creditUnionBlueprintFallback';
import {
  transformOpenToEveryoneRow,
  transformACCVerifiedRow,
  transformAssociationRoutesRow,
} from './creditUnionBlueprintHelpers';
import {
  Building2, Users, Shield, TrendingUp, CheckCircle, Search, Landmark,
  CreditCard, Banknote, ArrowRight, Info, Star, Heart, Briefcase, Target,
  Globe, BookOpen, Loader2, AlertCircle, X, ChevronRight, Filter, SlidersHorizontal,
  Sparkles, MapPin, DollarSign, Award, Lightbulb, ExternalLink, Copy, Check,
  RefreshCw, AlertTriangle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const BENEFIT_KEYS = [
  'memberOwned', 'relationshipUnderwriting', 'higherLimits',
  'softPull', 'pledgeLoan', 'businessPath',
];
const BENEFIT_ICONS = [Heart, Shield, TrendingUp, CreditCard, Banknote, Briefcase];
const CHECKLIST_KEYS = ['item1', 'item2', 'item3', 'item4', 'item5', 'item6'];
const TRANSITION_KEYS = ['phase1', 'phase2', 'phase3', 'phase4'];

// ===================== COMPONENT =====================
const CreditUnionBlueprint = () => {
  const { t } = useI18n();
  const BP = 'academy.blueprints';
  const CU = 'academy.creditUnionBlueprint';

  // Data state
  const [blueprintData, setBlueprintData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('open');
  const [searchTerm, setSearchTerm] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [confidenceFilter, setConfidenceFilter] = useState('all');
  const [restrictionFilter, setRestrictionFilter] = useState('all');
  const [associationFilter, setAssociationFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recommended');
  const [viewMode, setViewMode] = useState('recommended');
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('cu-blueprint-favorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [compareList, setCompareList] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [activeSection, setActiveSection] = useState('why');
  const [copiedId, setCopiedId] = useState(null);

  const applyFallbackBlueprint = useCallback(() => {
    setBlueprintData(CREDIT_UNION_BLUEPRINT_FALLBACK);
    setError(null);
  }, []);

  // Fetch published blueprint data from CMS; use local fallback when API is missing
  const fetchBlueprintData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let loaded = null;
      for (const slug of CREDIT_UNION_BLUEPRINT_SLUGS) {
        const response = await fetch(`${API_URL}/api/public/blueprints/${slug}`);
        if (response.ok) {
          loaded = await response.json();
          break;
        }
        if (response.status !== 404) {
          throw new Error(t(`${BP}.error.failedToFetch`, { status: response.status }, `Failed to fetch blueprint: ${response.status}`));
        }
      }
      if (loaded?.page) {
        setBlueprintData(loaded);
      } else {
        applyFallbackBlueprint();
      }
    } catch (err) {
      console.error('Error fetching blueprint:', err);
      applyFallbackBlueprint();
    } finally {
      setLoading(false);
    }
  }, [t, applyFallbackBlueprint]);

  useEffect(() => {
    fetchBlueprintData();
  }, [fetchBlueprintData]);

  // Save favorites to localStorage
  useEffect(() => {
    localStorage.setItem('cu-blueprint-favorites', JSON.stringify(favorites));
  }, [favorites]);

  // Scroll spy for sticky nav
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['why', 'access', 'checklist', 'transition'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Transform backend data into frontend-friendly format
  const transformedData = useMemo(() => {
    if (!blueprintData?.tables) return { open: [], acc: [], association: [] };

    const useLocalFallback = blueprintData._source === 'local_fallback';
    const result = { open: [], acc: [], association: [] };

    for (const table of blueprintData.tables) {
      const sectionKey = table.section_key || '';
      const rows = table.rows || [];

      if (sectionKey.includes('open_to_everyone')) {
        result.open = rows.map((row) => transformOpenToEveryoneRow(row, t, CU, useLocalFallback));
      } else if (sectionKey.includes('verified_acc') || sectionKey.includes('acc_credit_unions')) {
        result.acc = rows.map((row) => transformACCVerifiedRow(row, t, CU, useLocalFallback));
      } else if (sectionKey.includes('nonprofit') || sectionKey.includes('association')) {
        result.association = rows.map((row) => transformAssociationRoutesRow(row, t, CU, useLocalFallback));
      }
    }

    return result;
  }, [blueprintData, t]);

  // Get current dataset
  const getCurrentData = useCallback(() => {
    switch (activeTab) {
      case 'open': return transformedData.open;
      case 'acc': return transformedData.acc;
      case 'association': return transformedData.association;
      default: return transformedData.open;
    }
  }, [activeTab, transformedData]);

  // Filter and sort data
  const filteredData = useMemo(() => {
    let data = [...getCurrentData()];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(row => 
        row.name.toLowerCase().includes(term) ||
        row.notes.toLowerCase().includes(term) ||
        (row.howToJoin && row.howToJoin.toLowerCase().includes(term)) ||
        (row.bestForLabel && row.bestForLabel.toLowerCase().includes(term)) ||
        (row.association && row.association.toLowerCase().includes(term))
      );
    }

    // Tier filter (Open to Everyone)
    if (tierFilter !== 'all' && activeTab === 'open') {
      data = data.filter((row) => row.tierKey === tierFilter);
    }

    if (confidenceFilter !== 'all') {
      data = data.filter((row) => row.confidenceKey === confidenceFilter);
    }

    if (restrictionFilter !== 'all' && activeTab === 'acc') {
      data = data.filter((row) => row.restrictionGroupKey === restrictionFilter);
    }

    if (associationFilter !== 'all' && activeTab === 'association') {
      data = data.filter((row) => row.associationTypeKey === associationFilter);
    }

    // Sort
    if (viewMode === 'recommended' || sortBy === 'recommended') {
      data.sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0));
    } else {
      switch (sortBy) {
        case 'name_asc':
          data.sort((a, b) => a.name.localeCompare(b.name));
          break;
        case 'name_desc':
          data.sort((a, b) => b.name.localeCompare(a.name));
          break;
        case 'cost_low':
          data.sort((a, b) => {
            const getCost = (row) => {
              if (row.minDeposit) return parseInt(row.minDeposit.replace(/\D/g, '')) || 0;
              if (row.cost) {
                if (row.cost.toLowerCase() === 'free') return 0;
                return parseInt(row.cost.replace(/\D/g, '')) || 0;
              }
              return 999;
            };
            return getCost(a) - getCost(b);
          });
          break;
        case 'cost_high':
          data.sort((a, b) => {
            const getCost = (row) => {
              if (row.minDeposit) return parseInt(row.minDeposit.replace(/\D/g, '')) || 0;
              if (row.cost) {
                if (row.cost.toLowerCase() === 'free') return 0;
                return parseInt(row.cost.replace(/\D/g, '')) || 0;
              }
              return 0;
            };
            return getCost(b) - getCost(a);
          });
          break;
        default:
          break;
      }
    }

    return data;
  }, [getCurrentData, searchTerm, tierFilter, confidenceFilter, restrictionFilter, associationFilter, sortBy, viewMode, activeTab]);

  // Toggle favorite
  const toggleFavorite = (id) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
  };

  // Toggle compare
  const toggleCompare = (id) => {
    setCompareList(prev => {
      if (prev.includes(id)) return prev.filter(c => c !== id);
      if (prev.length >= 4) return prev;
      return [...prev, id];
    });
  };

  // Get compare items - search across all transformed data
  const compareItems = useMemo(() => {
    const allData = [...transformedData.open, ...transformedData.acc, ...transformedData.association];
    return compareList.map(id => allData.find(row => row.id === id)).filter(Boolean);
  }, [compareList, transformedData]);

  // Copy to clipboard
  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Reset filters on tab change
  useEffect(() => {
    setSearchTerm('');
    setTierFilter('all');
    setConfidenceFilter('all');
    setRestrictionFilter('all');
    setAssociationFilter('all');
    setSortBy('recommended');
  }, [activeTab]);

  // Scroll to section
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // ===================== LOADING STATE =====================
  if (loading) {
    return (
      <div className="cu-blueprint-page" data-testid="credit-union-blueprint">
        <div className="cu-loading">
          <Loader2 className="cu-spinner" size={48} />
          <p>{t(`${CU}.loading.directory`, null, t(`${BP}.loadingDirectory`, null, 'Loading credit union directory...'))}</p>
        </div>
      </div>
    );
  }

  // ===================== ERROR STATE =====================
  if (error) {
    return (
      <div className="cu-blueprint-page" data-testid="credit-union-blueprint">
        <div className="cu-error">
          <AlertTriangle size={48} />
          <h3>{t(`${BP}.error.title`)}</h3>
          <p>{error}</p>
          <button className="cu-retry-btn" onClick={fetchBlueprintData}>
            <RefreshCw size={16} /> {t(`${BP}.error.tryAgain`)}
          </button>
        </div>
      </div>
    );
  }

  // ===================== EMPTY STATE =====================
  if (!blueprintData || !blueprintData.page) {
    return (
      <div className="cu-blueprint-page" data-testid="credit-union-blueprint">
        <div className="cu-error">
          <AlertCircle size={48} />
          <h3>{t(`${BP}.error.notPublishedTitle`, null, 'Blueprint Not Published')}</h3>
          <p>{t(`${BP}.error.notPublishedBody`, null, 'This blueprint has not been published yet. Please check back later.')}</p>
        </div>
      </div>
    );
  }

  const totalRows = transformedData.open.length + transformedData.acc.length + transformedData.association.length;

  const tableConfig = {
    open: { titleKey: 'tables.openTitle', subtitleKey: 'tables.openSubtitle' },
    acc: { titleKey: 'tables.accTitle', subtitleKey: 'tables.accSubtitle' },
    association: { titleKey: 'tables.associationTitle', subtitleKey: 'tables.associationSubtitle' },
  };

  const currentConfig = tableConfig[activeTab];

  return (
    <div className="cu-blueprint-page" data-testid="credit-union-blueprint">
      {/* ============ HERO ============ */}
      <section className="cu-hero">
        <div className="cu-hero-content">
          <div className="cu-hero-left">
            <span className="cu-eyebrow">
              <span className="cu-logo-dot"></span>
              {t(`${CU}.hero.eyebrow`)}
            </span>
            <h1 className="cu-hero-title">{t(`${CU}.hero.title`)}</h1>
            <p className="cu-hero-desc">{t(`${CU}.hero.description`)}</p>
            <div className="cu-hero-badges">
              <span className="cu-badge"><span className="cu-dot"></span> {t(`${CU}.hero.badges.memberOwned`)}</span>
              <span className="cu-badge"><span className="cu-dot"></span> {t(`${CU}.hero.badges.relationshipBanking`)}</span>
              <span className="cu-badge"><span className="cu-dot"></span> {t(`${CU}.hero.badges.businessTransition`)}</span>
            </div>
            <div className="cu-data-source-badge">
              <CheckCircle size={14} />
              <span>{t(`${CU}.hero.catalogued`, { count: totalRows })}</span>
            </div>
          </div>
          <div className="cu-hero-right">
            <div className="cu-stat-card">
              <Building2 size={24} className="cu-stat-icon" />
              <div className="cu-stat-value">5,000+</div>
              <div className="cu-stat-label">{t(`${CU}.hero.stats.insuredCount`)}</div>
            </div>
            <div className="cu-stat-card">
              <Users size={24} className="cu-stat-icon" />
              <div className="cu-stat-value">140M+</div>
              <div className="cu-stat-label">{t(`${CU}.hero.stats.membersCount`)}</div>
            </div>
            <div className="cu-stat-card cu-stat-highlight">
              <Target size={24} className="cu-stat-icon" />
              <div className="cu-stat-value">{t(`${CU}.hero.stats.strategic`)}</div>
              <div className="cu-stat-label">{t(`${CU}.hero.stats.positioning`)}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STICKY NAV ============ */}
      <nav className="cu-sticky-nav">
        <div className="cu-sticky-nav-inner">
          <button 
            className={`cu-nav-btn ${activeSection === 'why' ? 'active' : ''}`}
            onClick={() => scrollToSection('why')}
          >
            <Lightbulb size={16} /> {t(`${CU}.nav.why`)}
          </button>
          <button 
            className={`cu-nav-btn ${activeSection === 'access' ? 'active' : ''}`}
            onClick={() => scrollToSection('access')}
          >
            <Globe size={16} /> {t(`${CU}.nav.access`)}
          </button>
          <button 
            className={`cu-nav-btn ${activeSection === 'checklist' ? 'active' : ''}`}
            onClick={() => scrollToSection('checklist')}
          >
            <CheckCircle size={16} /> {t(`${CU}.nav.checklist`)}
          </button>
          <button 
            className={`cu-nav-btn ${activeSection === 'transition' ? 'active' : ''}`}
            onClick={() => scrollToSection('transition')}
          >
            <Briefcase size={16} /> {t(`${CU}.nav.transition`)}
          </button>
        </div>
      </nav>

      {/* ============ WHY SECTION ============ */}
      <section className="cu-section" id="why">
        <div className="cu-section-header">
          <h2 className="cu-section-title">{t(`${CU}.sections.whyTitle`)}</h2>
          <p className="cu-section-subtitle">{t(`${CU}.sections.whySubtitle`)}</p>
        </div>

        <div className="cu-benefits-grid">
          {BENEFIT_KEYS.map((key, idx) => (
            <div key={key} className="cu-benefit-card">
              <div className="cu-benefit-icon">
                {React.createElement(BENEFIT_ICONS[idx], { size: 20 })}
              </div>
              <h3 className="cu-benefit-title">{t(`${CU}.benefits.${key}.title`)}</h3>
              <p className="cu-benefit-text">{t(`${CU}.benefits.${key}.text`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ ACCESS PATHS SECTION ============ */}
      <section className="cu-section cu-section-alt" id="access">
        <div className="cu-section-header">
          <h2 className="cu-section-title">{t(`${CU}.sections.accessTitle`)}</h2>
          <p className="cu-section-subtitle">{t(`${CU}.sections.accessSubtitle`)}</p>
        </div>

        <div className="cu-access-panel">
          {/* Tabs */}
          <div className="cu-tabs">
            <button 
              className={`cu-tab ${activeTab === 'open' ? 'active' : ''}`}
              onClick={() => setActiveTab('open')}
              data-testid="tab-open"
            >
              <Globe size={16} /> {t(`${CU}.tabs.open`)} ({transformedData.open.length})
            </button>
            <button 
              className={`cu-tab ${activeTab === 'acc' ? 'active' : ''}`}
              onClick={() => setActiveTab('acc')}
              data-testid="tab-acc"
            >
              <Award size={16} /> {t(`${CU}.tabs.acc`)} ({transformedData.acc.length})
            </button>
            <button 
              className={`cu-tab ${activeTab === 'association' ? 'active' : ''}`}
              onClick={() => setActiveTab('association')}
              data-testid="tab-association"
            >
              <Users size={16} /> {t(`${CU}.tabs.association`)} ({transformedData.association.length})
            </button>
          </div>

          {/* Controls */}
          <div className="cu-controls">
            <div className="cu-controls-top">
              {/* View Mode Toggle */}
              <div className="cu-view-toggle">
                <button 
                  className={viewMode === 'recommended' ? 'active' : ''}
                  onClick={() => setViewMode('recommended')}
                >
                  <Sparkles size={14} /> {t(`${CU}.filters.recommendedFirst`)}
                </button>
                <button 
                  className={viewMode === 'all' ? 'active' : ''}
                  onClick={() => setViewMode('all')}
                >
                  {t(`${CU}.filters.browseAll`)}
                </button>
              </div>

              {/* Search */}
              <div className="cu-search">
                <Search size={18} />
                <input 
                  type="text"
                  placeholder={t(`${CU}.filters.searchPlaceholder`)}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-input"
                />
                {searchTerm && (
                  <button className="cu-search-clear" onClick={() => setSearchTerm('')}>
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            <div className="cu-controls-bottom">
              <div className="cu-filters">
                {/* Tier Filter (Open tab) */}
                {activeTab === 'open' && (
                  <select 
                    value={tierFilter} 
                    onChange={(e) => setTierFilter(e.target.value)}
                    className="cu-filter-select"
                  >
                    <option value="all">{t(`${CU}.filters.allTiers`)}</option>
                    <option value="top_tier">{t(`${CU}.labels.tiers.top_tier`)}</option>
                    <option value="strong">{t(`${CU}.labels.tiers.strong`)}</option>
                    <option value="good">{t(`${CU}.labels.tiers.good`)}</option>
                  </select>
                )}

                {/* Restriction Filter (ACC tab) */}
                {activeTab === 'acc' && (
                  <select 
                    value={restrictionFilter} 
                    onChange={(e) => setRestrictionFilter(e.target.value)}
                    className="cu-filter-select"
                  >
                    <option value="all">{t(`${CU}.filters.allRestrictions`)}</option>
                    <option value="nationwide">{t(`${CU}.labels.restrictions.nationwide`)}</option>
                    <option value="regional">{t(`${CU}.labels.restrictions.regional`)}</option>
                    <option value="occupation_based">{t(`${CU}.labels.restrictions.occupation_based`)}</option>
                  </select>
                )}

                {/* Association Filter (Association tab) */}
                {activeTab === 'association' && (
                  <select 
                    value={associationFilter} 
                    onChange={(e) => setAssociationFilter(e.target.value)}
                    className="cu-filter-select"
                  >
                    <option value="all">{t(`${CU}.filters.allAssociations`)}</option>
                    <option value="financial_education">{t(`${CU}.labels.associationTypes.financial_education`)}</option>
                    <option value="charitable">{t(`${CU}.labels.associationTypes.charitable`)}</option>
                    <option value="historical_society">{t(`${CU}.labels.associationTypes.historical_society`)}</option>
                    <option value="space_science">{t(`${CU}.labels.associationTypes.space_science`)}</option>
                    <option value="free_subscription">{t(`${CU}.labels.associationTypes.free_subscription`)}</option>
                    <option value="recreation_parks">{t(`${CU}.labels.associationTypes.recreation_parks`)}</option>
                  </select>
                )}

                {/* Confidence Filter */}
                <select 
                  value={confidenceFilter} 
                  onChange={(e) => setConfidenceFilter(e.target.value)}
                  className="cu-filter-select"
                >
                  <option value="all">{t(`${CU}.filters.allConfidence`)}</option>
                  <option value="high">{t(`${CU}.filters.highOnly`)}</option>
                  <option value="medium">{t(`${CU}.filters.mediumOnly`)}</option>
                </select>

                {/* Sort */}
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="cu-filter-select"
                >
                  <option value="recommended">{t(`${CU}.filters.sortRecommended`)}</option>
                  <option value="name_asc">{t(`${CU}.filters.sortNameAsc`)}</option>
                  <option value="name_desc">{t(`${CU}.filters.sortNameDesc`)}</option>
                  <option value="cost_low">{t(`${CU}.filters.sortCostLow`)}</option>
                  <option value="cost_high">{t(`${CU}.filters.sortCostHigh`)}</option>
                </select>
              </div>

              <div className="cu-controls-right">
                <div className="cu-stats-pills">
                  <span className="cu-stat-pill">
                    {t(`${CU}.filters.results`, { count: filteredData.length })}
                  </span>
                  <span className="cu-stat-pill">
                    <Star size={12} /> {t(`${CU}.filters.saved`, { count: favorites.length })}
                  </span>
                </div>
                <button 
                  className={`cu-compare-btn ${compareList.length >= 2 ? 'active' : ''}`}
                  onClick={() => setShowCompareModal(true)}
                  disabled={compareList.length < 2}
                >
                  {t(`${CU}.filters.compareSelected`, { count: compareList.length })}
                </button>
              </div>
            </div>
          </div>

          {/* Table - Empty State for Tab */}
          {getCurrentData().length === 0 ? (
            <div className="cu-table-empty">
              <AlertCircle size={48} />
              <h3>{t(`${CU}.empty.noPublishedData`)}</h3>
              <p>{t(`${CU}.empty.noPublishedRows`)}</p>
            </div>
          ) : (
            <div className="cu-table-container">
              <div className="cu-table-header">
                <div>
                  <h3>{t(`${CU}.${currentConfig.titleKey}`)}</h3>
                  <p>{t(`${CU}.${currentConfig.subtitleKey}`)}</p>
                </div>
                <span className="cu-table-meta">
                  {viewMode === 'recommended' ? t(`${CU}.filters.showingRecommended`) : t(`${CU}.filters.showingAll`)}
                </span>
              </div>

              <div className="cu-table-wrap">
                <table className="cu-data-table">
                  <thead>
                    <tr>
                      <th className="cu-col-check"></th>
                      <th>{t(`${CU}.columns.creditUnion`)}</th>
                      {activeTab === 'open' && <th>{t(`${CU}.columns.howToJoin`)}</th>}
                      {activeTab === 'open' && <th>{t(`${CU}.columns.minDeposit`)}</th>}
                      {activeTab === 'acc' && <th>{t(`${CU}.columns.accAccess`)}</th>}
                      {activeTab === 'acc' && <th>{t(`${CU}.columns.stateRestriction`)}</th>}
                      {activeTab === 'association' && <th>{t(`${CU}.columns.association`)}</th>}
                      {activeTab === 'association' && <th>{t(`${CU}.columns.cost`)}</th>}
                      <th>{t(`${CU}.columns.notesHighlights`)}</th>
                      {activeTab === 'open' && <th>{t(`${CU}.columns.tier`)}</th>}
                      <th>{t(`${CU}.columns.confidence`)}</th>
                      <th>{t(`${CU}.columns.actions`)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length === 0 ? (
                      <tr>
                        <td colSpan={activeTab === 'open' ? 8 : 7} className="cu-empty-row">
                          <Search size={32} />
                          <p>{t(`${CU}.empty.noFilterResults`)}</p>
                          <button onClick={() => {
                            setSearchTerm('');
                            setTierFilter('all');
                            setConfidenceFilter('all');
                            setRestrictionFilter('all');
                            setAssociationFilter('all');
                          }}>{t(`${CU}.filters.clearFilters`)}</button>
                        </td>
                      </tr>
                    ) : (
                      filteredData.map((row) => (
                        <tr key={row.id} className={`cu-data-row ${row.recommended ? 'cu-recommended' : ''}`}>
                          <td className="cu-col-check">
                            <input 
                              type="checkbox"
                              checked={compareList.includes(row.id)}
                              onChange={() => toggleCompare(row.id)}
                              disabled={!compareList.includes(row.id) && compareList.length >= 4}
                              title={compareList.length >= 4 && !compareList.includes(row.id) ? t(`${CU}.labels.maxCompare`) : t(`${CU}.labels.addToCompare`)}
                            />
                          </td>
                          <td>
                            <div className="cu-name-cell">
                              <span className="cu-name">{row.name}</span>
                              {row.recommended && <span className="cu-recommended-badge"><Sparkles size={10} /> Recommended</span>}
                            </div>
                          </td>
                          {activeTab === 'open' && <td className="cu-join-cell">{row.howToJoin}</td>}
                          {activeTab === 'open' && <td><span className="cu-deposit">{row.minDeposit}</span></td>}
                          {activeTab === 'acc' && <td><span className="cu-acc-yes"><CheckCircle size={14} /> {row.accAccess}</span></td>}
                          {activeTab === 'acc' && (
                            <td>
                              <span className={`cu-restriction ${/^none\b/i.test((row.stateRestriction || '').trim()) ? 'cu-none' : ''}`}>
                                {row.stateRestriction}
                              </span>
                            </td>
                          )}
                          {activeTab === 'association' && <td className="cu-assoc-cell">{row.association}</td>}
                          {activeTab === 'association' && <td><span className={`cu-cost ${row.cost?.toLowerCase() === 'free' ? 'cu-free' : ''}`}>{row.cost}</span></td>}
                          <td className="cu-notes-cell">{row.notes}</td>
                          {activeTab === 'open' && (
                            <td>
                              <span className={`cu-tier-badge cu-tier-${row.tierClass}`}>
                                {row.tierLabel}
                              </span>
                            </td>
                          )}
                          <td>
                            <span className={`cu-confidence cu-conf-${row.confidenceKey}`}>
                              {row.confidenceLabel}
                            </span>
                          </td>
                          <td className="cu-actions-cell">
                            <button 
                              className={`cu-fav-btn ${favorites.includes(row.id) ? 'active' : ''}`}
                              onClick={() => toggleFavorite(row.id)}
                              title={favorites.includes(row.id) ? t(`${CU}.labels.removeFromSaved`) : t(`${CU}.labels.save`)}
                            >
                              <Star size={16} fill={favorites.includes(row.id) ? 'currentColor' : 'none'} />
                            </button>
                            <button 
                              className="cu-details-btn"
                              onClick={() => setSelectedRow(row)}
                            >
                              {t(`${CU}.labels.details`)} <ChevronRight size={14} />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="cu-table-footer">
                <span>{t(`${CU}.footer.tableFooterExplore`)}</span>
                <span>{t(`${CU}.footer.tableFooterCount`, { filtered: filteredData.length, total: getCurrentData().length })}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ============ CHECKLIST SECTION ============ */}
      <section className="cu-section" id="checklist">
        <div className="cu-section-header">
          <h2 className="cu-section-title">{t(`${CU}.sections.checklistTitle`)}</h2>
          <p className="cu-section-subtitle">{t(`${CU}.sections.checklistSubtitle`)}</p>
        </div>

        <div className="cu-checklist-grid">
          {CHECKLIST_KEYS.map((key, idx) => (
            <div key={key} className="cu-check-card">
              <div className="cu-check-num">{idx + 1}</div>
              <div>
                <h4 className="cu-check-title">{t(`${CU}.checklist.${key}.title`)}</h4>
                <p className="cu-check-text">{t(`${CU}.checklist.${key}.text`)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ TRANSITION SECTION ============ */}
      <section className="cu-section cu-section-alt" id="transition">
        <div className="cu-section-header">
          <h2 className="cu-section-title">{t(`${CU}.sections.transitionTitle`)}</h2>
          <p className="cu-section-subtitle">{t(`${CU}.sections.transitionSubtitle`)}</p>
        </div>

        <div className="cu-roadmap">
          {TRANSITION_KEYS.map((key, idx) => (
            <React.Fragment key={key}>
              <div className="cu-roadmap-card">
                <div className="cu-roadmap-num">{idx + 1}</div>
                <div>
                  <h4 className="cu-roadmap-title">{t(`${CU}.transition.${key}.title`)}</h4>
                  <p className="cu-roadmap-text">{t(`${CU}.transition.${key}.text`)}</p>
                </div>
              </div>
              {idx < TRANSITION_KEYS.length - 1 && <div className="cu-roadmap-arrow"><ArrowRight size={20} /></div>}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ============ DETAILS DRAWER ============ */}
      {selectedRow && (
        <>
          <div className="cu-drawer-backdrop" onClick={() => setSelectedRow(null)} />
          <div className="cu-drawer">
            <div className="cu-drawer-header">
              <div>
                <h3>{selectedRow.name}</h3>
                {selectedRow.recommended && (
                  <span className="cu-drawer-rec"><Sparkles size={12} /> {t(`${CU}.labels.recommended`)}</span>
                )}
              </div>
              <button onClick={() => setSelectedRow(null)}><X size={20} /></button>
            </div>
            
            <div className="cu-drawer-content">
              {/* Quick Stats */}
              <div className="cu-drawer-stats">
                {selectedRow.tierKey && (
                  <div className="cu-drawer-stat">
                    <span className="cu-drawer-stat-label">{t(`${CU}.labels.tier`)}</span>
                    <span className={`cu-tier-badge cu-tier-${selectedRow.tierClass}`}>
                      {selectedRow.tierLabel}
                    </span>
                  </div>
                )}
                <div className="cu-drawer-stat">
                  <span className="cu-drawer-stat-label">{t(`${CU}.columns.confidence`)}</span>
                  <span className={`cu-confidence cu-conf-${selectedRow.confidenceKey}`}>
                    {selectedRow.confidenceLabel}
                  </span>
                </div>
                {selectedRow.minDeposit && (
                  <div className="cu-drawer-stat">
                    <span className="cu-drawer-stat-label">{t(`${CU}.labels.minDeposit`)}</span>
                    <span className="cu-deposit">{selectedRow.minDeposit}</span>
                  </div>
                )}
                {selectedRow.cost && (
                  <div className="cu-drawer-stat">
                    <span className="cu-drawer-stat-label">{t(`${CU}.labels.cost`)}</span>
                    <span className={`cu-cost ${selectedRow.cost?.toLowerCase() === 'free' ? 'cu-free' : ''}`}>{selectedRow.cost}</span>
                  </div>
                )}
              </div>

              {/* Join Path */}
              <div className="cu-drawer-section">
                <h4><MapPin size={16} /> {t(`${CU}.labels.howToJoin`)}</h4>
                <p>{selectedRow.howToJoin || selectedRow.association || t(`${CU}.labels.seeNotesEligibility`)}</p>
              </div>

              {selectedRow.stateRestriction && (
                <div className="cu-drawer-section">
                  <h4><MapPin size={16} /> {t(`${CU}.labels.restrictions`)}</h4>
                  <p>
                    {/^none\b/i.test((selectedRow.stateRestriction || '').trim())
                      ? t(`${CU}.labels.noRestrictionsNationwide`)
                      : selectedRow.stateRestriction}
                  </p>
                </div>
              )}

              <div className="cu-drawer-section">
                <h4><Lightbulb size={16} /> {t(`${CU}.labels.publishedHighlights`)}</h4>
                <p>{selectedRow.notes}</p>
              </div>

              {selectedRow.bestForLabel && (
                <div className="cu-drawer-section">
                  <h4><Target size={16} /> {t(`${CU}.labels.bestFor`)}</h4>
                  <p>{selectedRow.bestForLabel}</p>
                </div>
              )}

              {selectedRow.whyItMattersLabel && (
                <div className="cu-drawer-section">
                  <h4><Heart size={16} /> {t(`${CU}.labels.whyItMatters`)}</h4>
                  <p>{selectedRow.whyItMattersLabel}</p>
                </div>
              )}

              {selectedRow.guidanceLabels && selectedRow.guidanceLabels.length > 0 && (
                <div className="cu-drawer-section">
                  <h4><CheckCircle size={16} /> {t(`${CU}.labels.guidance`)}</h4>
                  <ul className="cu-drawer-guidance">
                    {selectedRow.guidanceLabels.map((tip, idx) => (
                      <li key={idx}><CheckCircle size={12} /> {tip}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="cu-drawer-actions">
                <button 
                  className={`cu-drawer-fav ${favorites.includes(selectedRow.id) ? 'active' : ''}`}
                  onClick={() => toggleFavorite(selectedRow.id)}
                >
                  <Star size={16} fill={favorites.includes(selectedRow.id) ? 'currentColor' : 'none'} />
                  {favorites.includes(selectedRow.id) ? t(`${CU}.labels.savedLabel`) : t(`${CU}.labels.saveToFavorites`)}
                </button>
                <button 
                  className="cu-drawer-copy"
                  onClick={() => copyToClipboard(selectedRow.name, selectedRow.id)}
                >
                  {copiedId === selectedRow.id ? <Check size={16} /> : <Copy size={16} />}
                  {copiedId === selectedRow.id ? t(`${CU}.labels.copied`) : t(`${CU}.labels.copyName`)}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ============ COMPARE MODAL ============ */}
      {showCompareModal && compareItems.length >= 2 && (
        <>
          <div className="cu-modal-backdrop" onClick={() => setShowCompareModal(false)} />
          <div className="cu-modal">
            <div className="cu-modal-header">
              <h3>{t(`${CU}.labels.compareTitle`, { count: compareItems.length })}</h3>
              <button onClick={() => setShowCompareModal(false)}><X size={20} /></button>
            </div>
            
            <div className="cu-modal-content">
              <div className="cu-compare-grid" style={{ gridTemplateColumns: `repeat(${compareItems.length}, 1fr)` }}>
                {compareItems.map((item) => (
                  <div key={item.id} className="cu-compare-col">
                    <div className="cu-compare-name">
                      <strong>{item.name}</strong>
                      {item.recommended && <span className="cu-recommended-badge"><Sparkles size={10} /></span>}
                    </div>
                    
                    <div className="cu-compare-row">
                      <span className="cu-compare-label">{t(`${CU}.labels.joinPath`)}</span>
                      <span>{item.howToJoin || item.association || '—'}</span>
                    </div>
                    
                    <div className="cu-compare-row">
                      <span className="cu-compare-label">{t(`${CU}.labels.cost`)}</span>
                      <span>{item.minDeposit || item.cost || '—'}</span>
                    </div>
                    
                    <div className="cu-compare-row">
                      <span className="cu-compare-label">{t(`${CU}.labels.restriction`)}</span>
                      <span>
                        {item.stateRestriction
                          || (item.restrictionGroupLabel
                            || (item.restrictionGroupKey && t(`${CU}.labels.restrictions.${item.restrictionGroupKey}`))
                            || t(`${CU}.labels.none`))}
                      </span>
                    </div>
                    
                    <div className="cu-compare-row">
                      <span className="cu-compare-label">{t(`${CU}.labels.highlights`)}</span>
                      <span className="cu-compare-notes">{item.notes}</span>
                    </div>
                    
                    <div className="cu-compare-row">
                      <span className="cu-compare-label">{t(`${CU}.labels.bestFor`)}</span>
                      <span>{item.bestForLabel || '—'}</span>
                    </div>
                    
                    <div className="cu-compare-row">
                      <span className="cu-compare-label">{t(`${CU}.labels.whyItMatters`)}</span>
                      <span>{item.whyItMattersLabel || '—'}</span>
                    </div>
                    
                    <div className="cu-compare-row">
                      <span className="cu-compare-label">{t(`${CU}.columns.confidence`)}</span>
                      <span className={`cu-confidence cu-conf-${item.confidenceKey}`}>
                        {item.confidenceLabel}
                      </span>
                    </div>
                    
                    {item.tierKey && (
                      <div className="cu-compare-row">
                        <span className="cu-compare-label">{t(`${CU}.labels.tier`)}</span>
                        <span className={`cu-tier-badge cu-tier-${item.tierClass}`}>
                          {item.tierLabel}
                        </span>
                      </div>
                    )}

                    <button 
                      className="cu-compare-remove"
                      onClick={() => toggleCompare(item.id)}
                    >
                      <X size={14} /> {t(`${CU}.labels.remove`)}
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="cu-modal-footer">
              <button onClick={() => setCompareList([])}>{t(`${CU}.labels.clearAll`)}</button>
              <button className="cu-modal-close" onClick={() => setShowCompareModal(false)}>{t(`${CU}.labels.done`)}</button>
            </div>
          </div>
        </>
      )}

      {/* Footer */}
      <div className="cu-footer">
        {t(`${CU}.footer.catalogue`, { count: totalRows })}
      </div>
    </div>
  );
};

export default CreditUnionBlueprint;
